import { useId, useState } from 'react';
import { useHiddenInbox, useSetAside } from '../hooks/use-inbox.js';
import { hiddenLine } from '../utils/set-aside.js';
import { parisWhen, reminderChoices } from '../utils/paris-time.js';

// Set aside (ADR-039 section 2): the decision needs more first. The operator says in one line what is missing and may
// pick a reminder; the item leaves the list and comes back when it changes (the answer arrived) or at the reminder.
// The same form on an inbox row and on the page where the decision is taken.

const OTHER = 'Other';

/** Read at submit, never during render */
function isPast(iso: string): boolean {
  return Date.parse(iso) <= Date.now();
}
const input = 'w-full rounded-md border px-2 py-1 text-sm';
const inputStyle = { borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)', color: 'var(--color-text)' };
const button = 'rounded-md border px-2.5 py-1 text-sm focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-60';
const primary = { borderColor: 'var(--color-brand)', backgroundColor: 'var(--color-brand-dim)', color: 'var(--color-text)', outlineColor: 'var(--color-brand)' };
const quiet = { borderColor: 'var(--color-border)', color: 'var(--color-text-2)', outlineColor: 'var(--color-brand)' };

export function SetAsideForm({ serverId, itemId, onDone, onCancel }: { serverId: string; itemId: string; onDone: () => void; onCancel: () => void }) {
  const id = useId();
  const [reason, setReason] = useState('');
  const [choices] = useState(() => reminderChoices());
  const [reminder, setReminder] = useState<string | null>(null);
  // « Other »: a day and a time typed in the browser's time zone, within the 30 days a state is kept
  const [other, setOther] = useState('');
  const [openedAt] = useState(() => Date.now());
  const setAside = useSetAside(serverId);
  const typed = other ? new Date(other) : null;
  const typedOk = !!typed && !Number.isNaN(typed.getTime()) && typed.getTime() > openedAt && typed.getTime() - openedAt <= 30 * 86_400_000;
  const until = reminder === OTHER ? (typedOk ? typed!.toISOString() : undefined) : choices.find((c) => c.label === reminder)?.until.toISOString();
  const [late, setLate] = useState(false);
  // Checked again on submit: the form may have stayed open past the time typed
  const submit = () => {
    if (until && isPast(until)) { setLate(true); return; }
    setLate(false);
    send();
  };
  const send = () => setAside.mutate({ itemId, action: 'set-aside', reason: reason.trim(), ...(until ? { until } : {}) }, { onSuccess: () => onDone() });
  return (
    <form className="animate-fade-in max-w-xl space-y-2 text-sm" onSubmit={(e) => { e.preventDefault(); if (reason.trim()) submit(); }}>
      <label htmlFor={`${id}-reason`} className="block" style={{ color: 'var(--color-text-2)' }}>What is missing before you decide?</label>
      <input id={`${id}-reason`} className={input} style={inputStyle} maxLength={200} autoFocus value={reason} onChange={(e) => setReason(e.target.value)}
        placeholder="The CAB date, an answer from the requester" />
      <fieldset className="flex flex-wrap items-baseline gap-x-2 gap-y-1.5">
        <legend className="float-left mr-1 text-xs" style={{ color: 'var(--color-text-3)' }}>Reminder</legend>
        {[null, ...choices.map((c) => c.label), OTHER].map((label) => (
          <button key={label ?? 'none'} type="button" aria-pressed={reminder === label} onClick={() => setReminder(label)}
            className="rounded px-1.5 py-0.5 text-xs focus-visible:outline focus-visible:outline-2"
            style={reminder === label ? { color: 'var(--color-text)', border: '1px solid var(--color-brand)' } : { color: 'var(--color-text-2)', border: '1px solid var(--color-border-2)' }}>
            {label ?? 'None'}
          </button>
        ))}
        {reminder === OTHER && (
          <input type="datetime-local" aria-label="Reminder day and time" className="rounded-md border px-2 py-0.5 text-xs" style={inputStyle}
            value={other} onChange={(e) => setOther(e.target.value)} />
        )}
      </fieldset>
      {((reminder === OTHER && other && !typedOk) || late) && <p role="alert" className="text-xs" style={{ color: 'var(--color-error)' }}>Pick a time in the next 30 days.</p>}
      <p className="text-xs" style={{ color: 'var(--color-text-3)' }}>
        It comes back when it changes{until ? `, or on ${parisWhen(until)} at the latest` : ''}.
      </p>
      <div className="flex flex-wrap gap-2">
        <button type="submit" className={button} style={primary} disabled={!reason.trim() || (reminder === OTHER && !typedOk) || setAside.isPending}>{setAside.isPending ? 'Setting aside…' : 'Set aside'}</button>
        <button type="button" className={button} style={quiet} onClick={onCancel}>Cancel</button>
      </div>
      {setAside.isError && <p role="alert" className="text-xs" style={{ color: 'var(--color-error)' }}>Not set aside: {(setAside.error as Error).message}</p>}
    </form>
  );
}

/**
 * The third decision next to Approve and Reject on a page where a decision is taken: « Set aside » opens the form;
 * once set aside, why and until when, with « Bring back ».
 */
export function SetAsideControl({ serverId, itemId }: { serverId: string; itemId: string }) {
  const [open, setOpen] = useState(false);
  const formId = useId();
  const aside = useHiddenInbox(serverId).data?.find((i) => i.id === itemId);
  const bringBack = useSetAside(serverId);
  if (aside && !open) {
    return (
      <p role="status" className="flex basis-full flex-wrap items-baseline gap-x-2 text-xs" style={{ color: 'var(--color-text-2)' }}>
        <span>{aside.hidden.state === 'set-aside' && !aside.hidden.byAidev && aside.hidden.reason ? 'Set aside. ' : ''}{hiddenLine(aside.hidden)}.</span>
        <button type="button" className="hover:underline focus-visible:outline focus-visible:outline-2" style={{ color: 'var(--color-brand-light)' }}
          disabled={bringBack.isPending} onClick={() => bringBack.mutate({ itemId, action: 'bring-back' })}>
          Bring back
        </button>
        {bringBack.isError && <span role="alert" style={{ color: 'var(--color-error)' }}>Not brought back: {(bringBack.error as Error).message}</span>}
      </p>
    );
  }
  return (
    <>
      <button type="button" className={button} style={quiet} aria-expanded={open} aria-controls={formId} onClick={() => setOpen(!open)}>Set aside</button>
      {open && <div id={formId} className="basis-full"><SetAsideForm serverId={serverId} itemId={itemId} onDone={() => setOpen(false)} onCancel={() => setOpen(false)} /></div>}
    </>
  );
}
